import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Circle, Trophy, TrendingUp, Plus, X, Save, RefreshCw, Copy, ExternalLink, Link2, Paperclip, FileText, Trash2, Bell, BellOff, Zap, Inbox, ChevronDown, ChevronRight, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const clamp = (n) => Math.max(0, Math.min(100, n));

export default function ClientProgress({ secret }) {
  const [rows, setRows] = useState(null);
  const [ladder, setLadder] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({}); // client_id -> {current_bbb, target_bbb, tasks, dirty}
  const [uploadingKey, setUploadingKey] = useState(null); // `${cid}-${idx}`
  const [notifMode, setNotifMode] = useState("instant"); // "instant" | "digest"
  const [notifSaving, setNotifSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // client_ids
  const [openIds, setOpenIds] = useState(() => new Set()); // expanded client folders
  const toggleOpen = (cid) => setOpenIds((s) => {
    const n = new Set(s);
    if (n.has(cid)) n.delete(cid); else n.add(cid);
    return n;
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [preview, setPreview] = useState(null); // {url, mime, filename} inline doc preview
  // In-app confirmation modal — window.confirm() is blocked inside the preview
  // iframe (returns false silently), which is why deletes "did nothing".
  const [confirmState, setConfirmState] = useState(null); // {title, body, confirmLabel, busy, onConfirm}
  const fileRefs = { current: {} };

  const canPreview = (att) => {
    const m = (att?.mime || "").toLowerCase();
    const f = (att?.filename || "").toLowerCase();
    return m.startsWith("image/") || m === "application/pdf" || /\.(png|jpe?g|gif|webp|bmp|pdf)$/.test(f);
  };
  const previewAttachment = async (row, taskIdx, att) => {
    try {
      const res = await api.get(
        `/progress/${row.client_id}/task/${taskIdx}/attachment/${att.id}/download?secret=${encodeURIComponent(secret)}`,
        { responseType: "blob" }
      );
      const mime = att.mime || res.data.type || "application/octet-stream";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      setPreview({ url, mime, filename: att.filename || "document" });
    } catch { toast.error("Could not open preview."); }
  };
  const closePreview = () => {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const load = () => {
    api.get(`/admin/progress?secret=${encodeURIComponent(secret)}`)
      .then(({ data }) => { setRows(data.clients || []); setLadder(data.bbb_ladder || []); })
      .catch(() => toast.error("Could not load progress."));
    api.get(`/expenses/owner-config?secret=${encodeURIComponent(secret)}`)
      .then(({ data }) => setNotifMode(data.notification_mode || "instant"))
      .catch(() => { /* silent */ });
  };
  useEffect(() => { if (secret) load(); }, [secret]); // eslint-disable-line

  const toggleNotifMode = async () => {
    const next = notifMode === "digest" ? "instant" : "digest";
    setNotifSaving(true);
    try {
      const { data } = await api.put(`/expenses/owner-config?secret=${encodeURIComponent(secret)}`, {
        notification_mode: next,
      });
      setNotifMode(data.notification_mode || next);
      toast.success(next === "digest"
        ? "Switched to daily digest — one email per day."
        : "Switched to instant emails — one per upload.");
    } catch {
      toast.error("Could not update notification mode.");
    } finally {
      setNotifSaving(false);
    }
  };

  const toggleMute = async (row) => {
    const next = !row.notifications_muted;
    try {
      await api.put(`/admin/progress/${row.client_id}?secret=${encodeURIComponent(secret)}`, {
        client_id: row.client_id,
        business_name: row.business_name,
        notifications_muted: next,
      });
      setRows((list) => list.map((r) => (r.client_id === row.client_id ? { ...r, notifications_muted: next } : r)));
      toast.success(next ? "Muted — you won't get emails when this client uploads." : "Unmuted.");
    } catch {
      toast.error("Could not update mute setting.");
    }
  };

  const uploadAttachment = async (row, taskIdx, file) => {
    if (!file) return;
    const key = `${row.client_id}-${taskIdx}`;
    setUploadingKey(key);
    const form = new FormData();
    form.append("file", file);
    form.append("secret", secret);
    try {
      const { data } = await api.post(`/progress/${row.client_id}/task/${taskIdx}/attach`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRows((list) => list.map((r) => {
        if (r.client_id !== row.client_id) return r;
        const tasks = [...(r.tasks || [])];
        tasks[taskIdx] = { ...tasks[taskIdx], attachments: [...(tasks[taskIdx].attachments || []), data] };
        return { ...r, tasks };
      }));
      toast.success(`${file.name} attached.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed.";
      toast.error(msg);
    } finally { setUploadingKey(null); }
  };

  const downloadAttachment = async (row, taskIdx, att) => {
    try {
      const res = await api.get(
        `/progress/${row.client_id}/task/${taskIdx}/attachment/${att.id}/download?secret=${encodeURIComponent(secret)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.mime || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = att.filename || "file";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 800);
    } catch { toast.error("Download failed."); }
  };

  const removeAttachment = async (row, taskIdx, att) => {
    setConfirmState({
      title: "Remove attachment?",
      body: `Delete “${att.filename}” from this task. This cannot be undone.`,
      confirmLabel: "Remove",
      onConfirm: async () => {
        try {
          await api.delete(`/progress/${row.client_id}/task/${taskIdx}/attachment/${att.id}?secret=${encodeURIComponent(secret)}`);
          setRows((list) => list.map((r) => {
            if (r.client_id !== row.client_id) return r;
            const tasks = [...(r.tasks || [])];
            tasks[taskIdx] = { ...tasks[taskIdx], attachments: (tasks[taskIdx].attachments || []).filter((x) => x.id !== att.id) };
            return { ...r, tasks };
          }));
          toast.success("Removed.");
          setConfirmState(null);
        } catch { toast.error("Could not remove."); }
      },
    });
  };

  const draftFor = (row) => drafts[row.client_id] || {
    current_bbb: row.current_bbb || "",
    target_bbb: row.target_bbb || "",
    tasks: row.tasks || [],
    bbb_profile_url: row.bbb_profile_url || "",
    contact_email: row.contact_email || "",
    dirty: false,
  };
  const setDraft = (cid, patch) => {
    // Always resolve the base draft from the real row so partial edits (e.g. changing
    // only the BBB rating) never wipe unrelated fields like tasks or URL.
    const row = (rows || []).find((r) => r.client_id === cid);
    if (!row) return;
    setDrafts((d) => {
      const base = d[cid] || {
        current_bbb: row.current_bbb || "",
        target_bbb: row.target_bbb || "",
        tasks: row.tasks || [],
        bbb_profile_url: row.bbb_profile_url || "",
        contact_email: row.contact_email || "",
      };
      return { ...d, [cid]: { ...base, ...patch, dirty: true } };
    });
  };

  const save = async (row) => {
    const draft = draftFor(row);
    setSavingId(row.client_id);
    try {
      const { data } = await api.put(`/admin/progress/${row.client_id}?secret=${encodeURIComponent(secret)}`, {
        client_id: row.client_id,
        business_name: row.business_name,
        current_bbb: draft.current_bbb,
        target_bbb: draft.target_bbb,
        tasks: draft.tasks,
        bbb_profile_url: draft.bbb_profile_url,
        contact_email: draft.contact_email,
      });
      setRows((list) => list.map((r) => (r.client_id === row.client_id ? data : r)));
      setDrafts((d) => { const { [row.client_id]: _, ...rest } = d; return rest; });
      toast.success("Progress saved.");
    } catch {
      toast.error("Could not save.");
    } finally {
      setSavingId(null);
    }
  };

  const removeClient = async (row) => {
    const name = row.business_name || row.client_id;
    setConfirmState({
      title: "Remove from Progress?",
      body: `Remove ${name} from the Progress tracker. Their BBB history and tasks will be deleted. This cannot be undone.`,
      confirmLabel: "Remove client",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/progress/${encodeURIComponent(row.client_id)}`);
          setRows((list) => (list || []).filter((r) => r.client_id !== row.client_id));
          setDrafts((d) => { const { [row.client_id]: _, ...rest } = d; return rest; });
          setSelected((s) => { const n = new Set(s); n.delete(row.client_id); return n; });
          toast.success("Client removed from Progress.");
          setConfirmState(null);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not remove client.");
        }
      },
    });
  };

  const toggleSelect = (cid) => setSelected((s) => {
    const n = new Set(s); if (n.has(cid)) n.delete(cid); else n.add(cid); return n;
  });

  const bulkRemove = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setConfirmState({
      title: `Remove ${ids.length} client${ids.length === 1 ? "" : "s"}?`,
      body: `BBB history and tasks are deleted for each selected client. This cannot be undone.`,
      confirmLabel: `Remove ${ids.length}`,
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          const { data } = await api.post("/admin/progress/bulk-delete", { ids });
          setRows((list) => (list || []).filter((r) => !ids.includes(r.client_id)));
          setDrafts((d) => { const c = { ...d }; ids.forEach((id) => delete c[id]); return c; });
          setSelected(new Set());
          toast.success(`Removed ${data.deleted || ids.length} client${(data.deleted || ids.length) === 1 ? "" : "s"}.`);
          setConfirmState(null);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Bulk remove failed.");
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  const [checkingId, setCheckingId] = useState(null);
  const checkNow = async (row) => {
    setCheckingId(row.client_id);
    try {
      const { data } = await api.post(`/admin/progress/${row.client_id}/check?secret=${encodeURIComponent(secret)}`);
      setRows((list) => list.map((r) => (r.client_id === row.client_id ? data : r)));
      toast.success(`BBB check: ${data.current_bbb || "unchanged"}.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Check failed.";
      toast.error(msg);
    } finally {
      setCheckingId(null);
    }
  };

  const copyShareLink = (row) => {
    const base = window.location.origin;
    const url = `${base}/progress/${row.share_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Share link copied.")).catch(() => toast.error("Copy failed."));
  };

  const markRead = async (row) => {
    try {
      await api.post(`/admin/progress/${row.client_id}/mark-read?secret=${encodeURIComponent(secret)}`);
      setRows((list) => list.map((r) => (r.client_id === row.client_id ? { ...r, unread_uploads: 0 } : r)));
    } catch { /* silent */ }
  };

  const toggleTask = (row, idx) => {
    const draft = draftFor(row);
    const next = draft.tasks.map((t, i) => i === idx ? { ...t, done: !t.done } : t);
    setDraft(row.client_id, { tasks: next });
  };

  const addTask = (row) => {
    const label = window.prompt("New task label");
    if (!label?.trim()) return;
    const draft = draftFor(row);
    setDraft(row.client_id, { tasks: [...draft.tasks, { label: label.trim(), done: false }] });
  };

  const removeTask = (row, idx) => {
    const draft = draftFor(row);
    setDraft(row.client_id, { tasks: draft.tasks.filter((_, i) => i !== idx) });
  };

  const filtered = useMemo(() => rows || [], [rows]);

  if (!rows) {
    return <div className="py-14 flex items-center justify-center gap-3 text-[#1e3d3a]/55"><Loader2 className="h-4 w-4 animate-spin" /> Loading client progress…</div>;
  }
  if (rows.length === 0) {
    return <div className="py-14 text-center text-[#1e3d3a]/45 text-sm rounded-2xl border border-[#1e3d3a]/12 bg-white">No clients yet. Once an expense analysis is saved, they'll appear here.</div>;
  }

  return (
    <div data-testid="tab-content-progress" className="space-y-5">
      {confirmState && (
        <div
          data-testid="progress-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !bulkDeleting) setConfirmState(null); }}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Progress · confirm</p>
            <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">{confirmState.title}</h3>
            <p className="mt-2 text-sm text-[#1e3d3a]/70">{confirmState.body}</p>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                data-testid="progress-confirm-yes"
                onClick={confirmState.onConfirm}
                disabled={bulkDeleting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] bg-[#b23a48] text-white hover:bg-[#8f2a37] disabled:opacity-60 transition-colors duration-300"
              >
                {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                {confirmState.confirmLabel}
              </button>
              <button
                type="button"
                data-testid="progress-confirm-cancel"
                onClick={() => setConfirmState(null)}
                disabled={bulkDeleting}
                className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] transition-colors duration-300 px-3 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1e3d3a]/12 bg-white px-5 py-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">
          <Inbox className="h-3.5 w-3.5 text-[#c9a961]" />
          Upload notifications
        </div>
        <button
          type="button"
          data-testid="notif-mode-toggle"
          onClick={toggleNotifMode}
          disabled={notifSaving}
          className="inline-flex items-center gap-2 rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-4 py-1.5 text-xs text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-50"
          title={notifMode === "digest" ? "One digest email sent daily" : "Email fires on every client upload"}
        >
          {notifMode === "digest"
            ? <><Inbox className="h-3.5 w-3.5" strokeWidth={1.8} /> Daily digest</>
            : <><Zap className="h-3.5 w-3.5" strokeWidth={1.8} /> Instant emails</>}
          <span className="text-[#1e3d3a]/45 uppercase tracking-[0.18em]">· click to switch</span>
        </button>
      </div>
      {filtered.length > 0 && (
        <div
          data-testid="progress-bulk-bar"
          className="flex items-center gap-3 flex-wrap rounded-2xl border border-[#1e3d3a]/12 bg-white px-5 py-2.5"
        >
          <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60 cursor-pointer">
            <input
              type="checkbox"
              data-testid="progress-select-all"
              checked={selected.size > 0 && selected.size >= filtered.length}
              onChange={() => {
                const allIds = filtered.map((r) => r.client_id);
                setSelected((s) => allIds.every((id) => s.has(id)) ? new Set() : new Set(allIds));
              }}
              className="h-3.5 w-3.5 accent-[#a3823f] cursor-pointer"
            />
            {selected.size > 0 && selected.size >= filtered.length
              ? "Deselect all"
              : `Select all ${filtered.length}`}
          </label>
          <button
            type="button"
            data-testid="progress-toggle-all"
            onClick={() => setOpenIds((s) => s.size >= filtered.length ? new Set() : new Set(filtered.map((r) => r.client_id)))}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60 hover:text-[#a3823f] transition-colors duration-300"
          >
            {openIds.size >= filtered.length && filtered.length > 0
              ? <><ChevronRight className="h-3 w-3" /> Collapse all</>
              : <><ChevronDown className="h-3 w-3" /> Expand all</>}
          </button>
          {selected.size > 0 && (
            <>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f] tabular-nums">
                {selected.size} selected
              </span>
              <button
                type="button"
                data-testid="progress-bulk-remove"
                onClick={bulkRemove}
                disabled={bulkDeleting}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#b23a48] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#8f2a37] disabled:opacity-60 transition-colors duration-300"
              >
                {bulkDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" strokeWidth={2} />}
                Remove {selected.size} client{selected.size === 1 ? "" : "s"}
              </button>
              <button
                type="button"
                data-testid="progress-bulk-clear"
                onClick={() => setSelected(new Set())}
                className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors duration-300"
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      {filtered.map((row) => {
        const draft = draftFor(row);
        const completed = draft.tasks.filter((t) => t.done).length;
        const total = draft.tasks.length;
        const taskPct = total ? Math.round((completed / total) * 100) : 0;
        // Progress bar reflects BBB ladder position of the *current* draft values
        const ci = ladder.indexOf(draft.current_bbb);
        const ti = ladder.indexOf(draft.target_bbb);
        const bbbPct = (ci >= 0 && ti > 0) ? clamp(Math.round((ci / ti) * 100)) : 0;
        const isSaving = savingId === row.client_id;
        const isSel = selected.has(row.client_id);
        const isOpen = openIds.has(row.client_id);
        return (
          <div
            key={row.client_id}
            data-testid={`progress-card-${row.client_id}`}
            className={`rounded-2xl border p-6 ${isOpen ? "space-y-5" : ""} transition-colors duration-300 ${isSel ? "border-[#c9a961] bg-[#c9a961]/6" : "border-[#1e3d3a]/12 bg-white"} ${!isOpen ? "self-start" : ""}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <input
                  type="checkbox"
                  data-testid={`progress-select-${row.client_id}`}
                  checked={isSel}
                  onChange={() => toggleSelect(row.client_id)}
                  className="mt-1.5 h-3.5 w-3.5 accent-[#a3823f] cursor-pointer shrink-0"
                  title="Select for bulk remove"
                />
                <button
                  type="button"
                  data-testid={`progress-toggle-${row.client_id}`}
                  onClick={() => toggleOpen(row.client_id)}
                  className="min-w-0 text-left group"
                  title={isOpen ? "Collapse" : "Expand"}
                >
                  <h3 className="font-head text-lg text-[#1e3d3a] truncate flex items-center gap-1.5">
                    {isOpen
                      ? <ChevronDown className="h-4 w-4 shrink-0 text-[#a3823f] transition-transform duration-300" />
                      : <ChevronRight className="h-4 w-4 shrink-0 text-[#1e3d3a]/40 group-hover:text-[#a3823f] transition-colors duration-300" />}
                    <span className="truncate">{row.business_name || "Unnamed client"}</span>
                  </h3>
                  {isOpen ? (
                    <p className="mt-0.5 ml-[22px] text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 font-mono">{row.client_id.slice(0, 12)}…</p>
                  ) : (
                    <p className="mt-0.5 ml-[22px] text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/50">
                      {draft.current_bbb || "—"} → {draft.target_bbb || "—"} · {completed}/{total} tasks · {taskPct}%
                    </p>
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {row.unread_uploads > 0 && (
                  <button
                    type="button"
                    data-testid={`progress-unread-${row.client_id}`}
                    onClick={() => markRead(row)}
                    title="New client uploads — click to mark read"
                    className="inline-flex items-center gap-1 rounded-full bg-[#c9a961] text-[#1e3d3a] px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold hover:bg-[#a3823f] hover:text-white transition-colors duration-300"
                  >
                    <Bell className="h-3 w-3" strokeWidth={2} />
                    {row.unread_uploads} new
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`progress-mute-${row.client_id}`}
                  onClick={() => toggleMute(row)}
                  title={row.notifications_muted ? "Muted — click to unmute" : "Mute upload emails for this client"}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    row.notifications_muted
                      ? "border-[#b23a48]/40 bg-[#b23a48]/8 text-[#b23a48] hover:bg-[#b23a48]/14"
                      : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961] hover:text-[#a3823f]"
                  }`}
                >
                  {row.notifications_muted
                    ? <BellOff className="h-3 w-3" strokeWidth={1.8} />
                    : <Bell className="h-3 w-3" strokeWidth={1.8} />}
                </button>
                <button
                  type="button"
                  data-testid={`progress-remove-${row.client_id}`}
                  onClick={() => removeClient(row)}
                  title="Remove client from Progress"
                  className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/50 hover:border-[#b23a48]/40 hover:bg-[#b23a48]/8 hover:text-[#b23a48] transition-colors duration-300"
                >
                  <Trash2 className="h-3 w-3" strokeWidth={1.8} />
                </button>
                {row.updated_at && (
                  <span className="text-[10px] text-[#1e3d3a]/40">
                    Updated {new Date(row.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                )}
              </div>
            </div>

            {isOpen && (<>
            {/* BBB progress rail */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] flex items-center gap-1.5">
                  <TrendingUp className="h-3 w-3" /> BBB progress
                </span>
                <span className="text-xs text-[#1e3d3a]/50 tabular-nums">{bbbPct}%</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <label className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/60 px-3 py-2">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-[#1e3d3a]/55">Current</div>
                  <select
                    data-testid={`progress-current-${row.client_id}`}
                    value={draft.current_bbb}
                    onChange={(e) => setDraft(row.client_id, { current_bbb: e.target.value })}
                    className="mt-0.5 w-full bg-transparent font-head text-xl text-[#1e3d3a] focus:outline-none"
                  >
                    <option value="">—</option>
                    {ladder.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>
                <label className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-3 py-2">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-[#a3823f]">Target</div>
                  <select
                    data-testid={`progress-target-${row.client_id}`}
                    value={draft.target_bbb}
                    onChange={(e) => setDraft(row.client_id, { target_bbb: e.target.value })}
                    className="mt-0.5 w-full bg-transparent font-head text-xl text-[#1e3d3a] focus:outline-none"
                  >
                    <option value="">—</option>
                    {ladder.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>
              </div>
              <div className="relative h-2 rounded-full bg-[#eae7de] overflow-hidden">
                <motion.div
                  key={`${row.client_id}-${bbbPct}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${bbbPct}%` }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#c9a961] to-[#1e3d3a] rounded-full"
                />
                {bbbPct >= 100 && (
                  <Trophy className="absolute right-1 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#c9a961]" />
                )}
              </div>
              <div className="mt-1 flex justify-between text-[9px] text-[#1e3d3a]/40 uppercase tracking-[0.18em]">
                <span>{draft.current_bbb || "start"}</span>
                <span>{draft.target_bbb || "target"}</span>
              </div>
            </div>

            {/* Task checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Task checklist</span>
                <span className="text-xs text-[#1e3d3a]/50 tabular-nums">{completed}/{total} · {taskPct}%</span>
              </div>
              <ul className="space-y-1.5">
                {draft.tasks.map((t, i) => {
                  const upKey = `${row.client_id}-${i}`;
                  const uploading = uploadingKey === upKey;
                  const atts = t.attachments || [];
                  return (
                    <li key={i} className="rounded-lg hover:bg-[#eae7de]/40 transition-colors duration-300 px-1 py-1">
                      <div className="flex items-center gap-2 group">
                        <button
                          type="button"
                          data-testid={`progress-task-toggle-${row.client_id}-${i}`}
                          onClick={() => toggleTask(row, i)}
                          className="shrink-0 text-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                        >
                          {t.done
                            ? <CheckCircle2 className="h-4 w-4" strokeWidth={1.8} />
                            : <Circle className="h-4 w-4 text-[#1e3d3a]/30 group-hover:text-[#c9a961]" strokeWidth={1.6} />}
                        </button>
                        <span className={`flex-1 text-sm ${t.done ? "text-[#1e3d3a]/40 line-through" : "text-[#1e3d3a]"}`}>{t.label}</span>
                        <input
                          type="file"
                          ref={(el) => { fileRefs.current[upKey] = el; }}
                          className="hidden"
                          data-testid={`progress-attach-input-${row.client_id}-${i}`}
                          onChange={(e) => { if (e.target.files?.[0]) uploadAttachment(row, i, e.target.files[0]); e.target.value = ""; }}
                        />
                        <button
                          type="button"
                          data-testid={`progress-attach-${row.client_id}-${i}`}
                          onClick={() => fileRefs.current[upKey]?.click()}
                          disabled={uploading}
                          className="text-[#1e3d3a]/45 hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-40"
                          title="Attach file"
                        >
                          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" strokeWidth={1.6} />}
                        </button>
                        <button
                          type="button"
                          data-testid={`progress-task-remove-${row.client_id}-${i}`}
                          onClick={() => removeTask(row, i)}
                          className="opacity-0 group-hover:opacity-100 text-[#1e3d3a]/35 hover:text-[#b23a48] transition-all duration-300"
                          title="Remove task"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={1.6} />
                        </button>
                      </div>
                      {atts.length > 0 && (
                        <ul className="mt-1.5 ml-6 space-y-1">
                          {atts.map((att) => (
                            <li key={att.id} className="flex items-center gap-2 text-[11px] text-[#1e3d3a]/70">
                              <button
                                type="button"
                                data-testid={`progress-attach-download-${att.id}`}
                                onClick={() => downloadAttachment(row, i, att)}
                                className="inline-flex items-center gap-1 hover:text-[#a3823f] transition-colors duration-300 max-w-[240px]"
                              >
                                <FileText className="h-3 w-3 shrink-0" />
                                <span className="truncate">{att.filename}</span>
                              </button>
                              {canPreview(att) && (
                                <button
                                  type="button"
                                  data-testid={`progress-attach-preview-${att.id}`}
                                  onClick={() => previewAttachment(row, i, att)}
                                  className="text-[#1e3d3a]/35 hover:text-[#a3823f] transition-colors duration-300"
                                  title="Preview"
                                >
                                  <Eye className="h-3 w-3" strokeWidth={1.8} />
                                </button>
                              )}
                              <button
                                type="button"
                                data-testid={`progress-attach-remove-${att.id}`}
                                onClick={() => removeAttachment(row, i, att)}
                                className="text-[#1e3d3a]/35 hover:text-[#b23a48] transition-colors duration-300"
                                title="Remove"
                              >
                                <Trash2 className="h-3 w-3" strokeWidth={1.6} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                data-testid={`progress-task-add-${row.client_id}`}
                onClick={() => addTask(row)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
              >
                <Plus className="h-3.5 w-3.5" /> Add task
              </button>
            </div>

            {/* BBB profile URL + contact email + tools */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/40 px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 flex items-center gap-1"><Link2 className="h-3 w-3" /> BBB profile URL</div>
                <input
                  data-testid={`progress-bbb-url-${row.client_id}`}
                  type="url"
                  placeholder="https://www.bbb.org/..."
                  value={draft.bbb_profile_url}
                  onChange={(e) => setDraft(row.client_id, { bbb_profile_url: e.target.value })}
                  className="mt-0.5 w-full bg-transparent text-xs text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:outline-none"
                />
              </label>
              <label className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/40 px-3 py-2">
                <div className="text-[9px] uppercase tracking-[0.24em] text-[#1e3d3a]/55">Client email</div>
                <input
                  data-testid={`progress-email-${row.client_id}`}
                  type="email"
                  placeholder="owner@company.com"
                  value={draft.contact_email}
                  onChange={(e) => setDraft(row.client_id, { contact_email: e.target.value })}
                  className="mt-0.5 w-full bg-transparent text-xs text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:outline-none"
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2 -mt-1 text-[11px]">
              <button
                type="button"
                data-testid={`progress-check-${row.client_id}`}
                onClick={() => checkNow(row)}
                disabled={checkingId === row.client_id || !draft.bbb_profile_url}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-50"
              >
                {checkingId === row.client_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Check BBB now
              </button>
              <button
                type="button"
                data-testid={`progress-share-${row.client_id}`}
                onClick={() => copyShareLink(row)}
                disabled={!row.share_token}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 uppercase tracking-[0.2em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-50"
              >
                <Copy className="h-3 w-3" /> Copy client link
              </button>
              {row.last_checked_at && (
                <span className="text-[10px] text-[#1e3d3a]/45">Last checked {new Date(row.last_checked_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              )}
            </div>

            {/* History trend */}
            {row.history?.length > 0 && (
              <div className="text-[11px] text-[#1e3d3a]/60 flex flex-wrap items-center gap-1">
                <TrendingUp className="h-3 w-3 text-[#c9a961]" />
                {row.history.slice(-6).map((h, i, arr) => (
                  <span key={i} className="flex items-center gap-1">
                    <span className="font-medium text-[#1e3d3a]">{h.rating}</span>
                    <span className="text-[10px] text-[#1e3d3a]/45">{new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                    {i < arr.length - 1 && <span className="text-[#c9a961]">→</span>}
                  </span>
                ))}
              </div>
            )}

            {draft.dirty && (
              <button
                type="button"
                data-testid={`progress-save-${row.client_id}`}
                onClick={() => save(row)}
                disabled={isSaving}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                {isSaving ? "Saving…" : "Save changes"}
              </button>
            )}
            </>)}
          </div>
        );
      })}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-[#1e3d3a]/60 backdrop-blur-sm"
          data-testid="admin-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
        >
          <div className="relative w-full max-w-4xl h-[85vh] rounded-2xl bg-white shadow-2xl border border-[#c9a961]/25 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#1e3d3a]/10">
              <div className="min-w-0 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#a3823f] shrink-0" />
                <span className="truncate text-sm font-medium text-[#1e3d3a]">{preview.filename}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={preview.url}
                  download={preview.filename}
                  data-testid="admin-preview-download"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
                <button
                  type="button"
                  data-testid="admin-preview-close"
                  onClick={closePreview}
                  className="text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300 p-1"
                  aria-label="Close preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-[#eae7de]/40 overflow-auto flex items-center justify-center">
              {preview.mime.startsWith("image/") ? (
                <img src={preview.url} alt={preview.filename} className="max-w-full max-h-full object-contain" />
              ) : (
                <iframe src={preview.url} title={preview.filename} className="w-full h-full" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
